/**
 * Cliente de `GET /api/disponibilidad`.
 *
 * DIFERENCIA IMPORTANTE CON `cliente.ts`: ahí la lectura es tolerante porque el
 * contrato todavía no estaba escrito. Aquí NO lo es. El contrato de esta ruta
 * ya está fijado, así que cada campo se exige con su nombre y su tipo exactos.
 * Si el backend manda otra cosa, se lanza un error que dice qué campo falló, en
 * vez de rellenar con un valor plausible: un hueco disfrazado en una pantalla
 * de disponibilidad se convierte en una reservación que no existe.
 *
 * Los nombres de la respuesta son `snake_case`; los de este módulo son
 * `camelCase`, como el resto del código. La correspondencia está anotada campo
 * por campo.
 *
 * Las comprobaciones estrictas viven en `contrato.ts` y las comparte con el
 * cliente de reservaciones: dos copias de la misma comprobación acaban
 * divergiendo, y una divergencia así no se ve como un error sino como un hueco
 * en la pantalla.
 */

import {
  exigirBooleano,
  exigirEntero,
  exigirInstante,
  exigirLista,
  exigirOpcion,
  exigirRegistro,
  exigirTexto,
  exigirTextoONulo,
  exigirZonaHoraria,
} from './contrato'
import { mensajeDelCuerpo } from './errores'

export { ErrorDeContrato } from './contrato'

/**
 * Un día completo de 34 espacios es más trabajo que leer el catálogo, así que
 * el límite es más holgado que el de `cliente.ts`.
 */
const TIEMPO_LIMITE_MS = 8000

/* --- Tipos del contrato --------------------------------------------------- */

/** Por qué el día está abierto o cerrado. Es lo que decide qué se muestra. */
export type MotivoDisponibilidad = 'abierto' | 'dia_no_habil' | 'sin_horario'

/** Estado de un bloque de una hora. */
export type EstadoBloque = 'libre' | 'ocupado' | 'bloqueado'

const MOTIVOS: readonly MotivoDisponibilidad[] = [
  'abierto',
  'dia_no_habil',
  'sin_horario',
]

const ESTADOS: readonly EstadoBloque[] = ['libre', 'ocupado', 'bloqueado']

export interface BloqueDisponibilidad {
  /** `inicio`: instante UTC, ya convertido a Date. Se muestra en `zonaHoraria`. */
  inicio: Date
  /** `fin`: instante UTC, ya convertido a Date. */
  fin: Date
  /** `lugares_totales` */
  lugaresTotales: number
  /** `lugares_libres` */
  lugaresLibres: number
  /** `estado` */
  estado: EstadoBloque
}

export interface EspacioConBloques {
  /** `id`, por ejemplo `CUB-01`. */
  id: string
  /** `nombre` */
  nombre: string
  /** `tipo` */
  tipo: string
  /** `modo`, por ejemplo `por_lugar`. */
  modo: string
  /**
   * `ubicacion`. Se acepta NULL porque la columna del catálogo lo admite y un
   * espacio sin ubicación declarada es un hueco conocido, no una respuesta
   * inválida.
   */
  ubicacion: string | null
  /** `bloques` */
  bloques: BloqueDisponibilidad[]
}

export interface Disponibilidad {
  /** `fecha`, en formato `YYYY-MM-DD`. */
  fecha: string
  /** `zona_horaria`. Con esta zona se formatean TODAS las horas de la pantalla. */
  zonaHoraria: string
  /** `abierto` */
  abierto: boolean
  /** `motivo` */
  motivo: MotivoDisponibilidad
  /** `detalle`: texto del asueto cuando `motivo` es `dia_no_habil`. */
  detalle: string | null
  /** `abre`: hora local de apertura `HH:MM:SS`, o NULL si el día está cerrado. */
  abre: string | null
  /** `cierra`: hora local de cierre `HH:MM:SS`, o NULL si el día está cerrado. */
  cierra: string | null
  /** `espacios` */
  espacios: EspacioConBloques[]
}

export interface ParametrosDisponibilidad {
  fecha: string
  /** NULL o ausente = todos los espacios. */
  espacio?: string | null | undefined
}

/**
 * Prefijo de la clave de TanStack Query.
 *
 * Se exporta porque quien reserva tiene que invalidar la disponibilidad de
 * TODAS las fechas y espacios que haya en caché, no solo la que está viendo:
 * una reservación cambia el conteo de lugares libres de ese bloque para
 * cualquier vista que lo incluya. Con el prefijo, `invalidateQueries` las
 * alcanza todas.
 */
export const CLAVE_DISPONIBILIDAD = ['disponibilidad'] as const

/** Clave completa de una consulta. `undefined` y `''` son «todos los espacios». */
export function claveDeDisponibilidad(
  fecha: string,
  espacio: string | undefined,
): readonly [string, string, string] {
  return [CLAVE_DISPONIBILIDAD[0], fecha, espacio ?? '']
}

/* --- Petición ------------------------------------------------------------- */

/**
 * Consulta `GET /api/disponibilidad`. Lanza si la petición no llega, si el
 * backend responde con error o si la respuesta no cumple el contrato.
 */
export async function consultarDisponibilidad(
  parametros: ParametrosDisponibilidad,
): Promise<Disponibilidad> {
  const consulta = new URLSearchParams({ fecha: parametros.fecha })
  if (
    parametros.espacio !== undefined &&
    parametros.espacio !== null &&
    parametros.espacio !== ''
  ) {
    consulta.set('espacio', parametros.espacio)
  }
  const ruta = `/api/disponibilidad?${consulta.toString()}`

  let respuesta: Response
  try {
    respuesta = await fetch(ruta, {
      headers: { accept: 'application/json' },
      signal: AbortSignal.timeout(TIEMPO_LIMITE_MS),
    })
  } catch (error) {
    throw new Error(
      `No se pudo contactar al backend (${describirError(error)}).`,
    )
  }

  const cuerpo: unknown = await respuesta.json().catch(() => null)

  if (!respuesta.ok) {
    throw new Error(mensajeDeError(cuerpo, respuesta.status))
  }

  return leerDisponibilidad(cuerpo)
}

/**
 * El contrato promete `{"error": "..."}` en 400 y 404. Si no llegó así, se
 * reporta el código: es más honesto que inventar una causa.
 */
function mensajeDeError(cuerpo: unknown, estado: number): string {
  return (
    mensajeDelCuerpo(cuerpo) ??
    `El backend respondió ${estado} en /api/disponibilidad.`
  )
}

function describirError(error: unknown): string {
  if (error instanceof DOMException && error.name === 'TimeoutError') {
    return `sin respuesta en ${TIEMPO_LIMITE_MS / 1000} segundos`
  }
  if (error instanceof Error && error.message.trim() !== '') return error.message
  return 'error de red'
}

/* --- Lectura estricta ----------------------------------------------------- */

function leerDisponibilidad(cuerpo: unknown): Disponibilidad {
  const raiz = exigirRegistro(cuerpo, 'la respuesta')

  return {
    fecha: exigirTexto(raiz, 'fecha', 'la respuesta'),
    zonaHoraria: exigirZonaHoraria(raiz, 'zona_horaria', 'la respuesta'),
    abierto: exigirBooleano(raiz, 'abierto', 'la respuesta'),
    motivo: exigirOpcion(raiz, 'motivo', MOTIVOS, 'la respuesta'),
    detalle: exigirTextoONulo(raiz, 'detalle', 'la respuesta'),
    abre: exigirTextoONulo(raiz, 'abre', 'la respuesta'),
    cierra: exigirTextoONulo(raiz, 'cierra', 'la respuesta'),
    espacios: exigirLista(raiz, 'espacios', 'la respuesta').map(
      (fila, indice) => leerEspacio(fila, `espacios[${indice}]`),
    ),
  }
}

function leerEspacio(fila: unknown, ruta: string): EspacioConBloques {
  const registro = exigirRegistro(fila, ruta)

  return {
    id: exigirTexto(registro, 'id', ruta),
    nombre: exigirTexto(registro, 'nombre', ruta),
    tipo: exigirTexto(registro, 'tipo', ruta),
    modo: exigirTexto(registro, 'modo', ruta),
    ubicacion: exigirTextoONulo(registro, 'ubicacion', ruta),
    bloques: exigirLista(registro, 'bloques', ruta).map((bloque, indice) =>
      leerBloque(bloque, `${ruta}.bloques[${indice}]`),
    ),
  }
}

function leerBloque(fila: unknown, ruta: string): BloqueDisponibilidad {
  const registro = exigirRegistro(fila, ruta)

  return {
    inicio: exigirInstante(registro, 'inicio', ruta),
    fin: exigirInstante(registro, 'fin', ruta),
    lugaresTotales: exigirEntero(registro, 'lugares_totales', ruta),
    lugaresLibres: exigirEntero(registro, 'lugares_libres', ruta),
    estado: exigirOpcion(registro, 'estado', ESTADOS, ruta),
  }
}
